import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { DetailsConfigActions } from '../../AdminPanel/DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../../AdminPanel/DataDisplay/ExitConfigBlocker';
import { ConnectionDetailsSection } from '../components/ConnectionDetailsSection';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { LabeledTextField } from '../components/LabeledTextField';
import { SettingsCard } from '../components/SettingsCard';
import { ILevelFormValues } from './iLevelConfig.schema';
import { useILevelConfigFormState } from './useILevelConfigFormState';

export function ILevelConfiguration() {
  const { editStatus, isConnected, isLoading, successMessage, errorMessage, methods, onSave, onReset } =
    useILevelConfigFormState();

  const description =
    'Configure your iLevel integration credentials. Changes apply to your entire organization immediately after saving.';

  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' p='12px' gap='1rem' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <IntegrationDetailHeader appName={AppName.ILevel} description={description} />

        {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <SettingsCard>
          <ConnectionDetailsSection isConnected={isConnected} />

          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <ClientCredentialsFields isConnected={isConnected} />
          </Box>

          <Stack direction='row' gap='1rem' alignItems='center'>
            <DetailsConfigActions
              save={{ handler: onSave, disabled: editStatus !== 'modified' }}
              reset={{ handler: onReset }}
            />
          </Stack>
        </SettingsCard>
      </Stack>
    </FormProvider>
  );
}

function ClientCredentialsFields({ isConnected }: { isConnected: boolean }) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ILevelFormValues>();

  return (
    <Stack gap='1rem'>
      <Controller
        name='clientId'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='Client ID'
            required
            placeholder='Enter your iLevel Client ID'
            error={!!errors.clientId}
            helperText={errors.clientId?.message}
          />
        )}
      />
      <Controller
        name='clientSecret'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='Client Secret'
            description={
              isConnected
                ? 'Leave blank to keep existing secret. Enter a new value to update it.'
                : 'Securely stored.'
            }
            required={!isConnected}
            type='password'
            placeholder={
              isConnected ? 'Leave blank to keep existing secret' : 'Enter your iLevel Client Secret'
            }
            error={!!errors.clientSecret}
            helperText={errors.clientSecret?.message}
          />
        )}
      />
    </Stack>
  );
}
